import { ENTITY_TYPES_MAPPING, DIAG_TYPES } from '../utils/constants';
import { useRouter, useRoute } from 'vue-router';
import {
  useAdminStore,
  useFavoritesStore,
  useKnowledgeStore,
} from '@getmayday/vue-modules';
import useExternalRedirection from './useExternalRedirection';
import useNotification from './useNotification';

type EntityAction = {
  entityId: string;
  entityType: string;
  entityAncestors: string | null;
  lang: string;
};

const useEntities = () => {
  const router = useRouter();
  const route = useRoute();
  const configStore = useConfigStore();

  const getConfig = () => {
    const adminStore = useAdminStore();
    const {
      subdomainUrl,
      company,
      hasAnswerCopyPasteShortcut,
      hasArticleCopyPasteShortcut,
      hasModalClosureShortcut,
      salesForceFocusTab,
      manuallyTriggerActions,
      displayFullTitle,
      displayArticleInMainFrame,
      contentFontSize,
    } = storeToRefs(adminStore);

    const { forceMainFrame, hostApp, positionIsLeft, positionIsTop } =
      storeToRefs(configStore);

    return {
      subdomainUrl:
        subdomainUrl.value || (import.meta.env.VITE_APP_FRONT_URL as string),
      companyPreferences: company.value.preferences,
      hasAnswerCopyPasteShortcut: hasAnswerCopyPasteShortcut.value,
      hasArticleCopyPasteShortcut: hasArticleCopyPasteShortcut.value,
      hasModalClosureShortcut: hasModalClosureShortcut.value,
      salesForceFocusTab: salesForceFocusTab.value,
      manuallyTriggerActions: manuallyTriggerActions.value,
      displayFullTitle: displayFullTitle.value,
      displayArticleInMainFrame:
        forceMainFrame.value || displayArticleInMainFrame.value,
      contentFontSize: contentFontSize.value,
      hostApp: hostApp.value,
      positionIsLeft: positionIsLeft.value,
      positionIsTop: positionIsTop.value,
    };
  };

  const showEntity = ({
    id,
    type,
    source,
  }: {
    id: string;
    type: string;
    source?: string;
  }) => {
    return goToEntity({ id, type, deepLoading: true, force: false, source });
  };

  const goToEntity = ({
    id,
    type,
    force = false,
    skipOrdered = false,
    deepLoading = false,
    forceModal = false,
    showBack = false,
    fromModal = false,
    fromToast = false,
    hash = '',
    source,
  }: Entity) => {
    if (!ENTITY_TYPES_MAPPING[type]) return;

    const knowledgeStore = useKnowledgeStore();
    const adminStore = useAdminStore();

    const { knowledgeId, mainContent, path, modalContent } =
      storeToRefs(knowledgeStore);
    const { displayArticleInMainFrame } = storeToRefs(adminStore);
    const { forceMainFrame } = storeToRefs(configStore);

    source = source || 'navigation';

    // if type is Answer, entity come from modal so we want to display back button
    if (type === 'Answer')
      return router.push({
        query: { modalAnswerId: id, modalBack: fromModal ? 1 : 0 },
      });

    let modalBack = false;
    // Check if back button should be display on modal
    if (fromModal && showBack) modalBack = type === 'Article';

    // Check if back button should be display on main body
    let mainBack = false;
    if (showBack) {
      mainBack = isDiag(mainContent.value.type) && isDiag(type);
    }

    let modalContentId;
    if (
      type === 'Article' &&
      !displayArticleInMainFrame.value &&
      !forceMainFrame.value
    ) {
      // When clicking on an article : open modal with article
      modalContentId = id + hash;
      id =
        path.value.length > 1
          ? mainContent.value.id
          : (knowledgeId.value as string);
      type = path.value.length > 1 ? mainContent.value.type : 'Knowledge';
      hash = mainContent.value.hash || '';
    } else if (forceModal && showBack) {
      modalContentId = id + hash;
      id = mainContent.value.id;
      type = mainContent.value.type;
    } else if (forceModal && !fromModal) {
      // When we force the opening of a content in the modal
      // Set the content of the main body to the previous collection and force the deeploading so that the breadcrumb is updated for the main content
      modalContentId = mainContent.value.id + hash;

      const collection = path.value.at(-2);
      id = collection.id;
      type = 'Case';
      deepLoading = true;
      hash = '';
    } else if (fromModal && showBack && type !== 'Article') {
      // When a diagnosis is clicked from the modal
      // The content of the modal is kept and the diagnosis is opened in the main body with a deeploading to generate the breadcrumb
      modalContentId = modalContent.value.id;
      deepLoading = true;
    } else if (
      !!route.query.modalContentId &&
      (route.query.modalContentId as string).split('#')[0] !== id &&
      (route.query.modalContentId as string).split('#')[1] &&
      (type !== 'Knowledge' || hash)
    ) {
      // If we focus on a hash of the main content or
      // if we navigate inside a collection we want to keep the modal open
      modalContentId = route.query.modalContentId;
    } else if (fromModal) {
      // If click on content come from modal we keep content in modal
      return router.push({
        query: { modalContentId: id + hash, modalBack: modalBack ? 1 : 0 },
      });
    }

    const newRoute = {
      path: `/${ENTITY_TYPES_MAPPING[type]}/${id}`,
      query: {
        force: force ? 1 : 0,
        skipOrdered: skipOrdered ? 1 : 0,
        deepLoading: deepLoading ? 1 : 0,
        modalContentId,
        modalBack: modalBack ? 1 : 0,
        mainBack: mainBack ? 1 : 0,
        fromToast: fromToast ? 1 : 0,
        source,
      },
      hash,
    } as EntityRoute;

    router.push(newRoute);
  };

  const goToLastEntity = () => {
    const { lastEntityRoute } = storeToRefs(configStore);
    if (!lastEntityRoute.value)
      return router.push({
        path: '/',
        query: { force: '0' },
      });

    const { query } = lastEntityRoute.value;

    router.push({
      ...lastEntityRoute.value,
      query: { ...query, noSave: '1' },
    });
  };

  const forceModal = () => {
    const knowledgeStore = useKnowledgeStore();
    const { mainContent } = storeToRefs(knowledgeStore);

    const entity = {
      id: mainContent.value.id,
      type: mainContent.value.type,
      deepLoading: true,
      forceModal: true,
      hash: mainContent.value.hash,
    };
    goToEntity(entity);
  };

  const closeModal = () => {
    router.push({ query: {} });
  };

  const hasMainBackButton = computed(() => {
    return route.query.mainBack === '1';
  });

  const hasModalBackButton = computed(() => {
    return route.query.modalBack === '1';
  });

  const isDiag = (type: string) => {
    return DIAG_TYPES.includes(type);
  };

  const goBack = () => {
    const { previousEntityRoute } = storeToRefs(configStore);
    const { resetPath } = useKnowledgeStore();

    if (previousEntityRoute.value) {
      const { query } = previousEntityRoute.value;

      router.push({
        ...previousEntityRoute.value,
        query: { ...query, deepLoading: 1 },
      });
    } else {
      resetPath();
      router.push({ path: '/' });
    }

    configStore.flushLastTwoRoutes();
  };

  const toggleFavorite = (contentId: string): void => {
    const favoritesStore = useFavoritesStore();
    const { selectedLanguage } = useKnowledgeStore();

    const { notify } = useNotification();
    const isFavorite = !!favoritesStore.itemIsFavorite(
      contentId,
      selectedLanguage,
    );
    if (isFavorite) {
      favoritesStore.removeFavoriteContent(contentId, selectedLanguage);
    } else {
      favoritesStore.addUserFavorite(contentId, selectedLanguage);
    }

    const key = isFavorite ? 'remove-favorite' : 'add-favorite';
    notify(`knowledge.notification.${key}`);
  };

  const favoriteIds = computed(() => {
    const { favoriteIds } = useFavoritesStore();
    return favoriteIds;
  });

  const copyLink = ({
    entityId,
    entityType,
    entityAncestors,
    lang,
  }: EntityAction): void => {
    const { webUrl, entityUrl } = useExternalRedirection();
    const { notify } = useNotification();
    navigator.clipboard.writeText(
      webUrl(
        entityUrl({
          entityId,
          entityType,
          entityAncestors,
          lang,
        }),
      ),
    );

    notify(`knowledge.notification.copy-link`);
  };

  const openInWeb = ({
    entityId,
    entityType,
    entityAncestors,
    lang,
  }: EntityAction) => {
    const { selectedLanguage } = useKnowledgeStore();
    const { redirectUrl, entityUrl } = useExternalRedirection();
    const url = redirectUrl(
      entityUrl({
        entityId,
        entityType,
        entityAncestors,
        lang: lang || selectedLanguage,
      }),
    );

    if (!url) return;
    window.open(url, '_blank');
  };

  const goToFeedback = ({
    modalContentId,
    source,
    lang,
  }: {
    modalContentId: string;
    source: string;
    lang: string;
  }) => {
    router.push({
      path: '/feedbacks',
      query: {
        modalContentId,
        source,
        lang,
        new: 1,
      },
    });
  };

  const modalIsOpen = computed(
    () =>
      (!!route.query.modalAnswerId && route.query.modalAnswerId !== '-1') ||
      (!!route.query.modalContentId && route.query.modalContentId !== '-1'),
  );

  return {
    showEntity,
    goToEntity,
    goToLastEntity,
    toggleFavorite,
    favoriteIds,
    getConfig,
    copyLink,
    openInWeb,
    goToFeedback,
    forceModal,
    closeModal,
    hasMainBackButton,
    hasModalBackButton,
    goBack,
    modalIsOpen,
  };
};

export default useEntities;
